import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { WorkflowNode, NodeVariableMapping } from '@/schemas/workflow'

interface VariableMapperProps {
  nodes: WorkflowNode[]
  mappings: NodeVariableMapping[]
  onChange: (mappings: NodeVariableMapping[]) => void
}

const EMPTY_MAPPING: NodeVariableMapping = {
  source_node_id: '',
  source_output: '',
  target_node_id: '',
  target_input: '',
}

export function VariableMapper({ nodes, mappings, onChange }: VariableMapperProps) {
  const { t } = useTranslation('workflows')

  const handleAddMapping = () => {
    onChange([...mappings, { ...EMPTY_MAPPING }])
  }

  const handleRemoveMapping = (index: number) => {
    onChange(mappings.filter((_, i) => i !== index))
  }

  const handleMappingChange = (
    index: number,
    updates: Partial<NodeVariableMapping>
  ) => {
    const updated = mappings.map((mapping, i) =>
      i === index ? { ...mapping, ...updates } : mapping
    )
    onChange(updated)
  }

  const getNodeOutputs = (nodeId: string) => {
    const node = nodes.find((n) => n.id === nodeId)
    return node?.outputs || []
  }

  const getNodeInputs = (nodeId: string) => {
    const node = nodes.find((n) => n.id === nodeId)
    return node?.inputs || []
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold text-gray-200">
          {t('detail.schema.variableMappingTitle')}
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1 border-gray-700/50 text-gray-300"
          onClick={handleAddMapping}
          disabled={nodes.length === 0}
        >
          <Plus className="w-4 h-4" />
          {t('detail.schema.addMapping')}
        </Button>
      </div>

      {mappings.length === 0 ? (
        <p className="text-sm text-gray-500 italic py-4 text-center">
          {t('detail.schema.addMapping')}
        </p>
      ) : (
        <div className="space-y-3">
          {/* Header row */}
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_40px] gap-2 items-center text-xs text-gray-500 px-1">
            <span>{t('detail.schema.sourceNode')}</span>
            <span>{t('detail.schema.sourceOutput')}</span>
            <span>{t('detail.schema.targetNode')}</span>
            <span>{t('detail.schema.targetInput')}</span>
            <span />
          </div>

          {mappings.map((mapping, index) => {
            const sourceOutputs = getNodeOutputs(mapping.source_node_id)
            const targetInputs = getNodeInputs(mapping.target_node_id)

            return (
              <div
                key={index}
                className="grid grid-cols-[1fr_1fr_1fr_1fr_40px] gap-2 items-center"
              >
                {/* Source Node */}
                <Select
                  value={mapping.source_node_id || undefined}
                  onValueChange={(value) =>
                    handleMappingChange(index, {
                      source_node_id: value,
                      source_output: '',
                    })
                  }
                >
                  <SelectTrigger className="bg-white/5 border-gray-700/50 h-9 text-sm">
                    <SelectValue placeholder={t('detail.schema.sourceNode')} />
                  </SelectTrigger>
                  <SelectContent className="glass border-gray-700/50">
                    {nodes.map((node) => (
                      <SelectItem key={node.id} value={node.id} className="focus:bg-white/5">
                        {node.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Source Output */}
                <Select
                  value={mapping.source_output || undefined}
                  onValueChange={(value) =>
                    handleMappingChange(index, { source_output: value })
                  }
                  disabled={!mapping.source_node_id}
                >
                  <SelectTrigger className="bg-white/5 border-gray-700/50 h-9 text-sm">
                    <SelectValue placeholder={t('detail.schema.sourceOutput')} />
                  </SelectTrigger>
                  <SelectContent className="glass border-gray-700/50">
                    {sourceOutputs.map((output) => (
                      <SelectItem
                        key={output.name}
                        value={output.name}
                        className="focus:bg-white/5"
                      >
                        {output.name} ({output.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Target Node */}
                <Select
                  value={mapping.target_node_id || undefined}
                  onValueChange={(value) =>
                    handleMappingChange(index, {
                      target_node_id: value,
                      target_input: '',
                    })
                  }
                >
                  <SelectTrigger className="bg-white/5 border-gray-700/50 h-9 text-sm">
                    <SelectValue placeholder={t('detail.schema.targetNode')} />
                  </SelectTrigger>
                  <SelectContent className="glass border-gray-700/50">
                    {nodes.map((node) => (
                      <SelectItem key={node.id} value={node.id} className="focus:bg-white/5">
                        {node.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Target Input */}
                <Select
                  value={mapping.target_input || undefined}
                  onValueChange={(value) =>
                    handleMappingChange(index, { target_input: value })
                  }
                  disabled={!mapping.target_node_id}
                >
                  <SelectTrigger className="bg-white/5 border-gray-700/50 h-9 text-sm">
                    <SelectValue placeholder={t('detail.schema.targetInput')} />
                  </SelectTrigger>
                  <SelectContent className="glass border-gray-700/50">
                    {targetInputs.map((input) => (
                      <SelectItem
                        key={input.name}
                        value={input.name}
                        className="focus:bg-white/5"
                      >
                        {input.name} ({input.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Remove */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 text-gray-500 hover:text-red-400"
                  onClick={() => handleRemoveMapping(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
